import { useMediaQuery, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ELEMENT_POSITIONING, ELEMENT_SCOPE, ELEMENT_STATUS, ELEMENT_TYPE, TAB_STATUSES } from "@/constants/index.js";
import { useWidth, widthUtils } from "@/helpers";

import ElementDropdown from "./ElementDropdown";
import { ElementMemo } from "./ElementMemo";
import ElementLinkDialog from "../../../Dashboard/containers/MediaDialogs/ElementLinkDialog";
import { useUrlParams } from "../../../hooks";
import { refreshCurrentElement, resetCurrentElement, setElementsDialog } from "../../../store/actions";
import {
  currentElementSelector,
  currentMediaItemSelector,
  elementsDialogSelector,
  visualBuilderDataSelector,
} from "../../../store/selectors";
import { useElements } from "../../hooks/useElements";

const useStyles = makeStyles({
  pipe: {
    border: "1px solid rgba(255, 255, 255, 0.2)",
    margin: 0,
    marginRight: 4,
    padding: 0,
  },
  buttonContainer: {
    backgroundColor: "#333333",
    borderRadius: 28,
    marginBottom: 8,
    padding: 8,
    width: "368px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  linkBase: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    padding: 8,
    borderRadius: "20px",
    width: 40,
    height: 40,
    marginRight: 4,
  },
  contentGroupPositioning: {
    position: "absolute",
    zIndex: 7000,
    marginTop: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "center",
  },
  experienceGroupPositioning: {
    zIndex: 3000,
    marginTop: 10,
    textAlign: "center",
    display: "inline-flex",
    justifyContent: "flex-end",
    pointerEvents: "auto",
  },
  menuPositioning: {
    position: "absolute",
    zIndex: 3000,
    marginTop: 10,
    left: "50%",
    right: 0,
    textAlign: "center",
    marginLeft: -90,
    fontFamily: "Inter",
  },
  iconsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  formControlRoot: {
    marginBottom: "30px",
    "&:last-child": {
      marginBottom: 0,
    },
    "& .MuiOutlinedInput-root": {
      color: "white",
      "&::placeholder": {
        color: "#fff",
      },
      "& fieldset": {
        border: "1px solid rgba(255, 255, 255, 0.12)",
      },
      "&:hover fieldset": {
        border: "1.5px solid white",
      },
      "&.Mui-focused fieldset": {
        border: "1.5px solid white",
      },
    },
  },
  textFieldInput: {
    backgroundColor: "transparent",
    padding: "12px 16px",
  },
  textFieldLabel: {
    color: "#fff",
    "&.Mui-placeholder": {
      color: "#fff",
    },
    "&.Mui-focused": {
      color: "#fff",
    },
  },
  helperText: {
    color: "white",
  },
  elementLink: {
    width: 60,
    height: 60,
    backgroundColor: "#8f0339",
    padding: "6px 2px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    marginBottom: 8,
    marginLeft: 5,
  },
  elementContainer: {
    display: "flex",
    alignItems: "center",
    pointerEvents: "auto",
  },
  elementMenu: {
    width: 368,
    height: 204,
    backgroundColor: "#333333",
    color: "white",
    padding: 24,
    borderRadius: 0,
    marginTop: 8,
  },
  elementIconButton: {
    margin: 8,
  },
  mobileElements: {
    backgroundColor: "transparent",
    boxShadow: "none",
  },
});

export default function Elements(props) {
  const { elements, setAnchorEl } = props;

  const dispatch = useDispatch();
  const { isOpen: openElementDialog } = useSelector(elementsDialogSelector());

  const params = useUrlParams();
  const { propertyId, experienceId, tabStatus } = params;

  const { element } = useSelector(currentElementSelector);
  const { id: elementId, elementType } = element;
  const isMap = elementType === ELEMENT_TYPE.MAP;
  const { mediaItem } = useSelector(currentMediaItemSelector);
  const { id: mediaItemId } = mediaItem;
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { rightDrawerAutoHide, rightDrawer, locked } = visualBuilderData;
  const [isOverlapping, setIsOverlapping] = useState(false);
  const defaultDim = { width: null, height: null };
  const [windowDimensions, setWindowDimensions] = useState(defaultDim);
  const [widthAdjustment, setWidthAdjustment] = useState(0);
  const [contentGroup, setContentGroup] = useState(null);
  const [experienceGroup, setExperienceGroup] = useState(null);

  const theme = useTheme();
  const width = useWidth();
  const { isWidthDown } = widthUtils(theme);
  const mdDown = isWidthDown("md", width);

  const showSingleArray = useMediaQuery(`(max-width:${widthAdjustment > 0 ? widthAdjustment : 720}px)`);
  const singleElementView = isOverlapping || showSingleArray;

  const fabDropdown = useMediaQuery("(max-width:456px)");

  const noLabels = useMediaQuery("(max-width:1024px)");

  const [autoOpen, setAutoOpen] = useState(false);

  const [allElementAnchorEl, setAllElementAnchorEl] = useState(null);

  const fabRef = useRef();

  const lastElementRef = useCallback(
    (node) => {
      if (node !== null && elementId && !isMap) {
        setAnchorEl(node);
      }
    },
    [elementId, isMap],
  );

  const lastMapElementRef = useCallback(
    (node) => {
      if (node !== null && elementId && isMap) {
        setAnchorEl(node);
      }
    },
    [elementId, isMap],
  );
  const newRef = useRef(null);
  const refWidth = newRef?.current?.offsetWidth;
  const classes = useStyles({
    refWidth,
    tabStatus,
    rightDrawer,
    rightDrawerAutoHide,
  });

  useElements({
    propertyId,
    experienceId,
    mediaItemId,
    element,
    elements,
    fabDropdown,
    setAllElementAnchorEl,
    setAutoOpen,
    setWindowDimensions,
    contentGroup,
    experienceGroup,
    isOverlapping,
    setIsOverlapping,
    setWidthAdjustment,
    windowDimensions,
    singleElementView,
  });

  const handleContentGroup = useCallback(
    (node) => {
      if (node) {
        setContentGroup(node.getBoundingClientRect());
      }
    },
    [windowDimensions.width],
  );

  const handleExperienceGroup = useCallback(
    (node) => {
      if (node) {
        setExperienceGroup(node.getBoundingClientRect());
      }
    },
    [windowDimensions.width],
  );

  const isRelatedLinks = (data) => data.mediaItemId === mediaItemId || data.status === ELEMENT_STATUS.DRAFT;

  const contentElements = [TAB_STATUSES.COLLECT, TAB_STATUSES.HOMEBASE].includes(tabStatus)
    ? elements?.filter((el) => el.scope === ELEMENT_SCOPE.CONTENT && isRelatedLinks(el))
    : elements?.filter(
        (el) =>
          (el.type === ELEMENT_TYPE.LINK || el.type === ELEMENT_TYPE.VOICE_MEMO) &&
          el.positioning === ELEMENT_POSITIONING.CONTENT &&
          isRelatedLinks(el),
      );

  const experienceElements = ![TAB_STATUSES.COLLECT, TAB_STATUSES.HOMEBASE].includes(tabStatus)
    ? elements?.filter((el) => el.type !== ELEMENT_TYPE.MAP && el.positioning === ELEMENT_POSITIONING.EXPERIENCE)
    : [];

  const mapElements = elements?.filter((el) => el.type === ELEMENT_TYPE.MAP);

  const [experienceAnchorEl, setExperienceAnchorEl] = useState({});
  const open = Boolean(allElementAnchorEl);

  const handleEllipsisClick = (e) => {
    setAllElementAnchorEl(e.currentTarget);
  };

  const handleEllipsisClose = () => {
    setAllElementAnchorEl(null);
    setAutoOpen(false);
  };

  const handlePopoverToggle = (eid) => {
    if (openElementDialog) {
      dispatch(resetCurrentElement());
      dispatch(setElementsDialog(false));
    } else {
      const element = elements.find((el) => el.id === eid);
      dispatch(refreshCurrentElement(element));
      dispatch(setElementsDialog(true));
    }
  };

  const forcePopoverOpen = (eid) => (reference) => {
    const nextState = Object.keys(experienceAnchorEl).reduce((acc, cur) => {
      acc[cur] = null;
      return acc;
    }, {});

    nextState[eid] = reference;

    setExperienceAnchorEl(nextState);
  };

  const rightElements = [...mapElements, ...experienceElements];

  const speedElements = [...contentElements, ...rightElements];

  if (element.status === ELEMENT_STATUS.DRAFT && [ELEMENT_TYPE.LINK, ELEMENT_TYPE.VOICE_MEMO].includes(element.type)) {
    if (element.positioning === ELEMENT_POSITIONING.EXPERIENCE) {
      rightElements.push(element);
    }

    if (element.positioning === ELEMENT_POSITIONING.CONTENT) {
      contentElements.push(element);
    }
  }

  return (
    <>
      <ElementLinkDialog />
      {singleElementView ? (
        (contentElements.length > 0 || rightElements.length > 0) && (
          <ElementDropdown
            allElementAnchorEl={allElementAnchorEl}
            autoOpen={autoOpen}
            classes={classes}
            element={element}
            experienceAnchorEl={experienceAnchorEl}
            fabDropdown={fabDropdown}
            fabRef={fabRef}
            forcePopoverOpen={forcePopoverOpen}
            handleEllipsisClick={handleEllipsisClick}
            handleEllipsisClose={handleEllipsisClose}
            handleExperienceGroup={handleExperienceGroup}
            handlePopoverToggle={handlePopoverToggle}
            isMap={isMap}
            lastElementRef={lastElementRef}
            lastMapElementRef={lastMapElementRef}
            mediaItemId={mediaItemId}
            open={open}
            speedElements={speedElements}
            tabStatus={tabStatus}
            locked={locked}
          />
        )
      ) : (
        <>
          <div className={classes.contentGroupPositioning}>
            <div ref={handleContentGroup} className={classes.elementContainer}>
              {contentElements &&
                contentElements.map((el) => {
                  return (
                    <ElementMemo
                      tabStatus={tabStatus}
                      key={el.id}
                      elementId={el.id}
                      type={el.type}
                      newRef={lastElementRef}
                      anchor={experienceAnchorEl[el.id]}
                      elementPositioning="content"
                      mediaItemId={mediaItemId}
                      elementData={element.id && element.id === el.id ? element : el}
                      onPopoverToggle={handlePopoverToggle}
                      isOpen={el.id === element.id}
                      forcePopoverOpen={forcePopoverOpen(el.id)}
                      noLabels={noLabels}
                      singleElementView={singleElementView}
                      locked={locked}
                      mdDown={mdDown}
                    />
                  );
                })}
            </div>
          </div>
          <div className={classes.experienceGroupPositioning}>
            <div ref={handleExperienceGroup} className={classes.elementContainer}>
              {rightElements?.map((el) => {
                return (
                  <ElementMemo
                    tabStatus={tabStatus}
                    key={el.id}
                    newRef={isMap ? lastMapElementRef : lastElementRef}
                    anchor={experienceAnchorEl[el.id]}
                    elementPositioning="experience"
                    elementData={element.id && element.id === el.id ? element : el}
                    onPopoverToggle={handlePopoverToggle}
                    isOpen={el.id === element.id}
                    forcePopoverOpen={forcePopoverOpen(el.id)}
                    noLabels={noLabels}
                    locked={locked}
                    mdDown={mdDown}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
